import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import * as Constant from 'common/Constant';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import * as ReportActions from '../actions/actions';
import * as Action from '../favoriteReport/actions/action';
import ReportChip from './reportFilter';
import { IconX } from '@tabler/icons-react';

const ReportFilters = ({ setData, filter, onSelectionChange, isMobile, resetSelectionTrigger, persistSelection = false }) => {

  const legacies = useSelector((state) => state.fo.legacies);
  const reportTags = useSelector((state) => state.fo.reportTags);
  const currentMenu = useSelector((state) => state.favoriteReport.currentMenu);
  const persistedTags = useSelector((state) => state.favoriteReport.selectedLegacyTags);
  const [showClearAll,  setShowClearAll] = useState(false);

  const [totals, updateTotals] = useState({});
  const [tags, updateTags] = useState(reportTags);

  // Multi-select: store selected tag codes (exclude "All").
  // Restore the saved selection so it survives report open/close.
  const [selectedCodes, setSelectedCodes] = useState(() =>
    persistSelection && Array.isArray(persistedTags) ? persistedTags : []
  );
  const isMobileView = useMediaQuery('(max-width:767px)');

  useEffect(() => {
    updateTotals(_.groupBy(legacies, (item) => item.reportTag?.code));
  }, [legacies]);

  useEffect(() => {
    updateTags(_.sortBy(reportTags, (tag) => tag.order));
  }, [reportTags]);

  // Drop any selected code that no longer exists once tags are known (e.g. after a tenant switch).
  useEffect(() => {
    if (!persistSelection) return;
    if (!Array.isArray(reportTags) || reportTags.length === 0) return;
    const validCodes = new Set(reportTags.map((tag) => tag?.code));
    setSelectedCodes((prev) => {
      const next = prev.filter((code) => validCodes.has(code));
      return next.length === prev.length ? prev : next;
    });
  }, [persistSelection, reportTags]);

  // Persist the current selection so it survives the report open/close remount.
  useEffect(() => {
    if (!persistSelection) return;
    store.dispatch(Action.setStandardSelectedTags(selectedCodes));
  }, [persistSelection, selectedCodes]);

  useEffect(() => {
    store.dispatch(ReportActions.fetchReportTags());
  }, []);

  useEffect(() => {
    if (persistSelection) return;
    setSelectedCodes([]);
    setShowClearAll(false);
  }, [persistSelection, resetSelectionTrigger]);

  // Apply filtering based on selectedCodes (multi-select)
  useEffect(() => {
    if (!Array.isArray(legacies)) {
      setData([]);
      return;
    }
    if (selectedCodes.length === 0) {
      setData(legacies); // "All"
    } else {
      const filtered = legacies.filter((item) =>
        selectedCodes.includes(item?.reportTag?.code)
      );
      setData(filtered);
    }
  }, [selectedCodes, legacies, setData]);

  // Sync "All" chip with menu; deselect All when any other chip is selected
  useEffect(() => {
    if (currentMenu?.name === Constant.ALL) {
      setSelectedCodes([]); // selecting All clears selections
    }
  }, [currentMenu]);

  useEffect(() => {
    if (selectedCodes.length > 0 && currentMenu?.name === Constant.ALL) {
      store.dispatch(Action.setCurrentMenuItem({ name: null }));
    }
  }, [selectedCodes, currentMenu]);

  // Re-show "Clear All" whenever chips are selected; stays hidden only while dismissed
  useEffect(() => {
    if (!isMobileView && selectedCodes.length > 0) {
      setShowClearAll(true);
    }
  }, [selectedCodes]);

  // Notify parent with the selected tag objects ([] means All)
  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      const selected =
        Array.isArray(tags) ? tags.filter((t) => selectedCodes.includes(t?.code)) : [];
      onSelectionChange(selected);
    }
  }, [selectedCodes, tags, onSelectionChange]);

  const toggleCode = (code) =>
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  // Get chip width based on device
  const getChipWidth = () => {
    if (isMobileView) return '100%';
    return 'auto';
  };

  return (   
    <>
      <Stack
        direction={isMobileView ? 'column' : 'row'}
        columnGap='12px'
        rowGap='12px'      
        sx={{
          alignItems: isMobileView ? 'flex-start' : 'center',
          ml: 'auto',
          flexWrap: isMobileView ? 'nowrap' : 'wrap',
          width: '100%',
          minHeight: 'auto'
        }}
      >
        {Array.isArray(tags) &&
          tags.map((item) => {
            const code = item?.code;
            const isSelected = selectedCodes.includes(code);
            return (
              <ReportChip
                key={item.id}
                item={item}
                filter={filter}
                total={totals[item.code]?.length ?? 0}
                selected={isSelected}
                onToggle={() => toggleCode(code)}
                isMobile={isMobileView}
                chipWidth={getChipWidth()}
              />
            );
          })}
        {showClearAll && selectedCodes.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap:'4px', 
            color: '#2F54A5', 
            marginTop:'5px',
            height: '24px',
            '&:hover': {
              color: '#068DF1'
            } }}
            onClick={() => setSelectedCodes([])}
          >
            <IconX
              height={16} 
              width={16} 
              style={{cursor:'pointer'}} 
            />
            <Typography
              sx={{ 
                cursor: 'pointer',
                fontFamily: 'Noto Sans', 
                fontSize: '14px', 
                fontStyle: 'normal', 
                fontWeight: 600, 
                height: '24px',
                lineHeight: '150%',
                width: '57px', 
                letterSpacing: '0',
                marginTop: '2px'
              }}>
              Clear All
            </Typography>
          </Box>
        )}
      </Stack>
    </>
  );
};
export default ReportFilters;